import {
  ActionDrawerItem,
  MobileActionDrawer,
} from '@components/MobileActionDrawer/MobileActionDrawer';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { frostedGlassTransitionProps } from '@lib/utils/frostedPopoverTransition';
import {
  CancelOutlined,
  DeleteOutlineRounded,
  ListAltRounded,
  PhotoLibraryOutlined,
  RefreshRounded,
} from '@mui/icons-material';
import { ListItemIcon, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';

interface BatchCardMenuProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  libraryUrl: string;
  /** Show the "Cancel batch" item — true while the batch is queued,
   * preparing, or actively uploading bytes. False once we've crossed into
   * wrap-up (chunks done, finishFileUpload in flight) and beyond. */
  canCancel: boolean;
  /** Show the "Delete batch" item — true only during server-side processing,
   * where every media row exists on the backend and can be safely deleted. */
  canDelete: boolean;
  /** Batch is in *any* active phase (queued / preparing / uploading /
   * wrapping-up / processing). Drives whether "Remove from list" is shown:
   * we never want it on an in-flight batch. */
  isActive: boolean;
  onRemove: () => void;
  /** Failures the user can still retry or view — the card's `retryableCount`. */
  retryableCount: number;
  /** Opens the Results modal filtered to this batch. Undefined hides the item. */
  onViewFailedFiles?: () => void;
  /** Re-queues failed files for this batch. Undefined hides the item. */
  onRetryFailed?: () => void;
  /** Opens the confirm-delete flow for every media in the batch. Undefined hides. */
  onDeleteBatch?: () => void;
  /** Cancels every in-flight upload in the batch. Undefined hides. */
  onCancelBatch?: () => void;
  /** Media count in this batch — drives the singular/plural "Cancel upload(s)". */
  mediaCount: number;
}

type BatchActionId =
  | 'show-in-library'
  | 'delete-batch'
  | 'retry-failed'
  | 'view-failed'
  | 'cancel-batch'
  | 'remove';

const itemSx = {
  fontSize: 13,
  borderRadius: '8px',
  padding: '8px 10px',
  margin: '0 0 2px 0',
  '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
};
const iconSx = { minWidth: 24, color: 'rgba(255, 255, 255, 0.85)' };
const dangerSx = { color: '#d64550', '&:hover': { background: 'rgba(214, 69, 80, 0.12)' } };
const dangerIconSx = { minWidth: 24, color: '#d64550' };

/**
 * Kebab actions for a batch card. Desktop renders a MUI `Menu` anchored to the
 * kebab; mobile renders a `MobileActionDrawer` (per context-menu-actions.md).
 * Both are driven by the SAME `actions` array + `handleAction` dispatcher, so
 * items can never drift between the two presentations.
 */
export function BatchCardMenu({
  anchor,
  onClose,
  libraryUrl,
  canCancel,
  canDelete,
  isActive,
  onRemove,
  retryableCount,
  onViewFailedFiles,
  onRetryFailed,
  onDeleteBatch,
  onCancelBatch,
  mediaCount,
}: BatchCardMenuProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const open = Boolean(anchor);

  // Shared action list — order matches the desktop menu composition:
  //   Always: Show in library
  //   Processing: Delete media
  //   Done + has fails: Retry failed / View failed files
  //   Cancellable: Cancel upload(s)
  //   Done: Remove from list
  const actions: (ActionDrawerItem<BatchActionId> & { run: () => void })[] = [];
  actions.push({
    id: 'show-in-library',
    label: 'Show in library',
    icon: <PhotoLibraryOutlined fontSize="small" />,
    run: () => navigate(libraryUrl),
  });
  if (canDelete && onDeleteBatch) {
    actions.push({
      id: 'delete-batch',
      label: 'Delete media',
      icon: <DeleteOutlineRounded fontSize="small" />,
      danger: true,
      run: onDeleteBatch,
    });
  }
  if (!isActive && retryableCount > 0 && onRetryFailed) {
    actions.push({
      id: 'retry-failed',
      label: retryableCount > 1 ? `Retry failed (${retryableCount})` : 'Retry failed',
      icon: <RefreshRounded fontSize="small" />,
      run: onRetryFailed,
    });
  }
  if (!isActive && retryableCount > 0 && onViewFailedFiles) {
    actions.push({
      id: 'view-failed',
      label: 'View failed files',
      icon: <ListAltRounded fontSize="small" />,
      run: onViewFailedFiles,
    });
  }
  if (canCancel && onCancelBatch) {
    actions.push({
      id: 'cancel-batch',
      label: mediaCount > 1 ? 'Cancel uploads' : 'Cancel upload',
      icon: <CancelOutlined fontSize="small" />,
      danger: true,
      run: onCancelBatch,
    });
  }
  if (!isActive) {
    actions.push({
      id: 'remove',
      label: 'Remove from list',
      icon: <DeleteOutlineRounded fontSize="small" />,
      danger: true,
      run: onRemove,
    });
  }

  const handleAction = (id: BatchActionId) => {
    onClose();
    actions.find((a) => a.id === id)?.run();
  };

  if (isMobile) {
    return (
      <MobileActionDrawer<BatchActionId>
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        title="Upload options"
        actions={actions.map(({ id, label, icon, danger }) => ({ id, label, icon, danger }))}
        onAction={handleAction}
      />
    );
  }

  return (
    <Menu
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        transition: frostedGlassTransitionProps,
        list: { sx: { padding: '6px' } },
        paper: {
          sx: {
            background: 'rgba(30, 30, 30, 0.85)',
            backdropFilter: 'blur(50px)',
            WebkitBackdropFilter: 'blur(50px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            minWidth: '220px',
            marginTop: '4px',
            boxShadow:
              '0 24px 32px -12px rgba(18, 18, 18, 0.6), 0 8px 24px -8px rgba(0, 0, 0, 0.4)',
          },
        },
      }}
    >
      {actions.map((a) => (
        <MenuItem
          key={a.id}
          sx={a.danger ? { ...itemSx, ...dangerSx } : itemSx}
          onClick={() => handleAction(a.id)}
        >
          <ListItemIcon sx={a.danger ? dangerIconSx : iconSx}>{a.icon}</ListItemIcon>
          {a.label}
        </MenuItem>
      ))}
    </Menu>
  );
}
